/**
 * Diagramas do projeto — a gestão.
 *
 * **Esta camada não desenha, e continua não desenhando.** O `Desenhar()` do
 * legado segue sem port. O que mudou é que ver o traço deixou de depender dele:
 * o desenho **gravado** vive em `desenhoDoDiagrama.ts`, lido da coluna `svg`,
 * que é de onde o site do cliente serve.
 *
 * Cuidado com o nome: o `GET {id}/svg` daqui lista *anexos* de SVG pendurados no
 * diagrama, **não** o desenho dele. São coisas diferentes e a ficha as separa.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api, noProjeto, obter } from "./cliente"
import type { PaginaResponse } from "./tipos"

export type DiagramaResponse = {
  id: number
  projetoId: number
  componenteId: number
  /** Resolvidos pela API — a tela não tem como, o catálogo dela é de uma página. */
  componenteCodigo: string
  componenteDescricao: string
  modo: number
  posicao: string
  versao: number
  sistemaId: number
  cor: string
  publicado: boolean
  publicadoData: string | null
  revisao: boolean
  revisaoData: string | null
  obs: string | null
  modelos: number[]
  /**
   * Os dois seguintes não aparecem em lista nenhuma: vêm para o formulário poder
   * devolvê-los intactos. `PUT api/diagramas/{id}` é substituição completa dos
   * sete metadados, e quem não os lê grava zero por cima — mudar a posição
   * apagaria o recorte de pinos e o desenho do componente principal.
   */
  pinos: string | null
  diagramaDesenhoInternoId: number
}

/** Os sete metadados que o `PUT` substitui de uma vez. */
export type MetadadosDoDiagrama = {
  modo: number
  posicao: string
  versao: number
  pinos: string | null
  sistemaId: number
  cor: string
  diagramaDesenhoInternoId: number
}

/** Monta o corpo a partir da linha, para nada se perder no caminho. */
export function metadadosDe(d: DiagramaResponse): MetadadosDoDiagrama {
  return {
    modo: d.modo,
    posicao: d.posicao,
    versao: d.versao,
    pinos: d.pinos,
    sistemaId: d.sistemaId,
    cor: d.cor,
    diagramaDesenhoInternoId: d.diagramaDesenhoInternoId,
  }
}

/**
 * Cria o diagrama. **Não desenha nada**: nasce vazio, e é o `Gerar` que monta
 * componentes e ligações a partir do pinout.
 *
 * `componenteId` é o componente **principal** — o que fica no meio do desenho e
 * de quem os outros penduram. `versao 2` é a do motor de hoje; a 1 existe no
 * acervo antigo e a tela não oferece.
 */
export function usarCriarDiagrama(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (corpo: {
      componenteId: number
      modo: number
      posicao: string
      versao: number
      sistemaId: number
      cor: string
      modelos: number[]
    }) => {
      const r = await api.post<DiagramaResponse>(
        "api/diagramas", { projetoId, ...corpo }, noProjeto(projetoId))
      return r.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagramas", projetoId] }),
  })
}

/** **Substituição completa** dos sete metadados — use `metadadosDe` para montar o corpo. */
export function usarEditarDiagrama(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, corpo }: { id: number; corpo: MetadadosDoDiagrama }) => {
      const r = await api.put<DiagramaResponse>(`api/diagramas/${id}`, corpo, noProjeto(projetoId))
      return r.data
    },
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
      qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", v.id] })
    },
  })
}

/**
 * A ordem dos componentes, uma coluna de cada vez.
 *
 * As duas listas são independentes — `left` e `right` são os dois lados do
 * componente principal — e a rota só faz UPDATE: manda os ids na ordem
 * desejada. Mandar só uma coluna deixa a outra como está.
 */
export function usarOrdenarComponentes(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (colunas: { left?: number[]; right?: number[] }) => {
      const q = new URLSearchParams()
      if (colunas.left?.length) q.set("left", colunas.left.join(","))
      if (colunas.right?.length) q.set("right", colunas.right.join(","))
      await api.put(`api/diagramas/${diagramaId}/ordem?${q}`, undefined, noProjeto(projetoId))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["diagrama-componentes", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
    },
  })
}

/** O que o publicar devolve quando encontra pendência (RN-08). */
export type DiscrepanciaRow = Record<string, unknown>
export type PublicarResponse = { publicado: boolean; discrepancias: DiscrepanciaRow[] }

/** Um diagrama só — a tela de montar precisa do sistema dele para os posicionamentos. */
export function usarDiagrama(diagramaId: number) {
  return useQuery({
    queryKey: ["diagrama", diagramaId] as const,
    enabled: diagramaId > 0,
    staleTime: 10_000,
    queryFn: () => obter<DiagramaResponse>(`api/diagramas/${diagramaId}`, {}),
  })
}

export function usarDiagramas(projetoId: number, pagina: number) {
  return useQuery({
    queryKey: ["diagramas", projetoId, pagina] as const,
    enabled: projetoId > 0,
    queryFn: () => obter<PaginaResponse<DiagramaResponse>>("api/diagramas", { projetoId, pagina }),
  })
}

export function usarPublicarDiagrama(projetoId: number) {
  return useMutation({
    mutationFn: async ({ id, publicar }: { id: number; publicar: boolean }) =>
      (await api.post<PublicarResponse | null>(
        `api/diagramas/${id}/${publicar ? "publicar" : "despublicar"}`,
        undefined, noProjeto(projetoId))).data,
  })
}

export function usarMandarParaRevisao(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, enviar }: { id: number; enviar: boolean }) => {
      await api.post(
        `api/diagramas/${id}/${enviar ? "enviar-revisao" : "remover-revisao"}`,
        undefined, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagramas", projetoId] }),
  })
}

export function usarObsDiagrama(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, obs }: { id: number; obs: string }) => {
      await api.patch(`api/diagramas/${id}/obs`, { obs }, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagramas", projetoId] }),
  })
}

/**
 * `gerar` preenche o diagrama a partir dos pinouts — é o que monta os
 * componentes e as ligações. **Reescreve o conteúdo do diagrama**, então a tela
 * pede confirmação antes.
 */
/**
 * **Gerar INSERE, não reescreve.** Ele monta componentes e ligações a partir do
 * pinout e não apaga o que já está lá: rodar duas vezes no mesmo diagrama duplica
 * cada componente. Quem refaz do zero é o **Restaurar**, que limpa antes.
 */
export function usarGerarDiagrama(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.post(`api/diagramas/${id}/gerar`, undefined, noProjeto(projetoId))
      return id
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
      // Sem isto a lista de componentes continua dizendo "0 no diagrama" depois
      // de gerar, que foi o que apareceu na primeira vez que a tela rodou.
      qc.invalidateQueries({ queryKey: ["diagrama-componentes", id] })
      qc.invalidateQueries({ queryKey: ["diagrama-desenho-estado", id] })
      qc.invalidateQueries({ queryKey: ["diagrama-verificacao", id] })
    },
  })
}

export function usarDuplicarDiagrama(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await api.post(`api/diagramas/${id}/duplicar`, undefined, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagramas", projetoId] }),
  })
}

export function usarExcluirDiagrama(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, restaurar }: { id: number; restaurar: boolean }) => {
      if (restaurar) await api.post(`api/diagramas/${id}/restaurar`, undefined, noProjeto(projetoId))
      else await api.delete(`api/diagramas/${id}`, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagramas", projetoId] }),
  })
}


// ------------------------------------------------- modelos, sistemas, lixeira

/**
 * Modelos a que o diagrama serve.
 *
 * É o mesmo vínculo que a RN-01 usa para esconder pinout: um diagrama que vale
 * só para o motor 1.6 não deve aparecer para quem abriu o 1.8. A resposta do
 * diagrama já traz a lista; isto é o outro lado, o de definir.
 */
export function usarDefinirModelosDoDiagrama(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, modelos }: { id: number; modelos: number[] }) => {
      await api.put(`api/diagramas/${id}/modelos`, { modelos }, noProjeto(projetoId))
    },
    onSuccess: (_r, { id }) => {
      qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
      qc.invalidateQueries({ queryKey: ["diagrama-modelos", id] })
    },
  })
}

/**
 * Os modelos do diagrama, lidos do próprio diagrama.
 *
 * **Não dá para usar `modelos` da linha da lista**: a listagem não carrega os
 * modelos (seria uma consulta por diagrama) e eles vêm sempre vazios. Marcar
 * uma caixa a partir dali gravava só aquele modelo — o `PUT` substitui o
 * conjunto — e apagava os que o diagrama já tinha.
 */
export function usarModelosDoDiagrama(diagramaId: number) {
  return useQuery({
    queryKey: ["diagrama-modelos", diagramaId] as const,
    enabled: diagramaId > 0,
    queryFn: async () => (await obter<DiagramaResponse>(`api/diagramas/${diagramaId}`, {})).modelos ?? [],
  })
}

/** Os sistemas do diagrama — devolve só os ids. */
export function usarSistemasDoDiagrama(diagramaId: number) {
  return useQuery({
    queryKey: ["diagrama-sistemas", diagramaId] as const,
    enabled: diagramaId > 0,
    queryFn: () => obter<number[]>(`api/diagramas/${diagramaId}/sistemas`, {}),
  })
}

export function usarVincularSistema(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (sistemaId: number) => {
      await api.post(`api/diagramas/${diagramaId}/sistemas`, { sistemaId }, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagrama-sistemas", diagramaId] }),
  })
}

/** Tira o sistema do diagrama. Não apaga sistema nem diagrama: só o vínculo. */
export function usarDesvincularSistema(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (sistemaId: number) => {
      await api.delete(`api/diagramas/${diagramaId}/sistemas/${sistemaId}`, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagrama-sistemas", diagramaId] }),
  })
}

/**
 * Conferir e concluir a revisão **por sistema dentro do projeto**.
 *
 * Repare no escopo: não é o diagrama que se conclui, é o sistema no projeto —
 * "a injeção deste projeto está revisada". Por isso estas ações só aparecem
 * depois de o diagrama ter sistema vinculado: sem `sistemaId` não há o que
 * marcar, e era exatamente o que faltava para esta parte existir.
 */
export type MarcaDeRevisao = "concluir" | "conferir" | "remover-conclusao" | "remover-conferencia"

export function usarMarcarRevisao(projetoId: number) {
  return useMutation({
    mutationFn: async ({ marca, sistemaId }: { marca: MarcaDeRevisao; sistemaId: number }) => {
      await api.post(
        `api/diagramas-sistema/${marca}`, undefined,
        noProjeto(projetoId, { params: { projetoId, sistemaId } }),
      )
    },
  })
}

/** Um componente ou ligação que foi excluído do diagrama — a lixeira (RN-15). */
export type ExcluidoDoDiagrama = {
  diagramaComponenteId: number
  componenteId: number
  titulo: string
  posicao: string
  ordem: number
  ligacoes: { diagramaLigacaoId: number; descricao?: string }[]
}

export function usarExcluidosDoDiagrama(diagramaId: number) {
  return useQuery({
    queryKey: ["diagrama-excluidos", diagramaId] as const,
    enabled: diagramaId > 0,
    queryFn: () => obter<ExcluidoDoDiagrama[]>(`api/diagramas/${diagramaId}/excluidos`, {}),
  })
}

export function usarRestaurarDoDiagrama(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ tipo, id }: { tipo: "componentes" | "ligacoes"; id: number }) => {
      await api.post(`api/diagramas/${tipo}/${id}/restaurar`, undefined, noProjeto(projetoId))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["diagrama-excluidos", diagramaId] })
      qc.invalidateQueries({ queryKey: ["diagramas", projetoId] })
    },
  })
}


/**
 * O diagrama a que este continua.
 *
 * Um diagrama grande demais para uma folha é quebrado em dois, e o vínculo é o
 * que diz qual é a outra metade — no projeto 976, o 7133 aponta para o 7129.
 * Sem isso, quem abre um dos dois não tem como saber que existe o resto.
 *
 * A resposta traz `diagramaId2: null` quando não há vínculo, e é assim que a
 * tela distingue "não tem" de "não carregou".
 */
export function usarVinculoDoDiagrama(diagramaId: number) {
  return useQuery({
    queryKey: ["diagrama-vinculo", diagramaId] as const,
    enabled: diagramaId > 0,
    queryFn: () => obter<{ diagramaId2: number | null }>(`api/diagramas/${diagramaId}/vinculo`, {}),
  })
}

export function usarVincularDiagrama(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (diagramaId2: number) => {
      await api.post(`api/diagramas/${diagramaId}/vinculo`, { diagramaId2 }, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagrama-vinculo", diagramaId] }),
  })
}

// ------------------------------------------------------------------ anexos SVG

/**
 * Um anexo SVG do diagrama — **não é o desenho dele**, é material que alguém
 * pendurou ali. São 730 em produção; a observação antiga de que "nenhum diagrama
 * tem anexo" era artefato de uma amostra de 100.
 */
export type AnexoSvg = { id: number; diagramaId: number; ordem: number; svg: string | null; descricao: string | null }

export function usarAnexosSvg(diagramaId: number) {
  return useQuery({
    queryKey: ["diagrama-svg", diagramaId] as const,
    queryFn: () => obter<AnexoSvg[]>(`api/diagramas/${diagramaId}/svg`, {}),
    enabled: diagramaId > 0,
  })
}

export function usarDescreverAnexo(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ svgId, descricao }: { svgId: number; descricao: string }) => {
      await api.put(`api/diagramas/${diagramaId}/svg/${svgId}`, { descricao }, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagrama-svg", diagramaId] }),
  })
}

export function usarExcluirAnexo(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (svgId: number) => {
      await api.delete(`api/diagramas/${diagramaId}/svg/${svgId}`, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagrama-svg", diagramaId] }),
  })
}

/**
 * A ordem inteira, e não um passo de cada vez.
 *
 * A API recusa lista incompleta de propósito: o sistema antigo renumera só o que
 * vem e deixa o resto com a ordem velha, o que dá duas fatias começando em 1.
 */
export function usarOrdenarAnexos(projetoId: number, diagramaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (svgIds: number[]) => {
      await api.put(`api/diagramas/${diagramaId}/svg/ordem`, { svgIds }, noProjeto(projetoId))
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagrama-svg", diagramaId] }),
  })
}

/**
 * A cor do componente principal — **do diagrama**, não de um componente avulso.
 *
 * 4.800 dos 4.829 diagramas estão no padrão `#E6E7E8`. Trocar marca o diagrama
 * para atualização, como no legado: sem isso o site segue mostrando o desenho
 * publicado com a cor velha.
 */
export function usarTrocarCorDoDiagrama(projetoId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, cor }: { id: number; cor: string }) => {
      const r = await api.put<{ cor: string }>(`api/diagramas/${id}/cor`, { cor }, noProjeto(projetoId))
      return r.data.cor
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["diagramas", projetoId] }),
  })
}
